"use client"

import { cn } from "@/lib/utils"
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react"

type BarebonesRevealProps = {
  children: ReactNode
  className?: string
  /** Stagger delay in ms for child cascade */
  delay?: number
  /** Slightly different entrance: up | left | scale */
  from?: "up" | "left" | "scale"
}

export const BarebonesReveal = ({
  children,
  className,
  delay = 0,
  from = "up",
}: BarebonesRevealProps) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.16, rootMargin: "0px 0px -8% 0px" }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={cn(
        "bb-reveal",
        `bb-reveal-${from}`,
        visible && "bb-reveal-in",
        className
      )}
      style={{ "--bb-reveal-delay": `${delay}ms` } as CSSProperties}
    >
      {children}
    </div>
  )
}
